import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectCoverflow } from 'swiper/modules';
import { useTranslation } from 'react-i18next';
import { projects } from '../data/projects';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/autoplay';

const getLocalized = (project: any, field: string, lang: string): string => {
  if (lang === 'zh' && project[`${field}_zh`]) return project[`${field}_zh`];
  if (lang === 'en' && project[`${field}_en`]) return project[`${field}_en`];
  return project[field] || '';
};

const Hero: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const handleProjectClick = (project: typeof projects[0]) => {
    window.dispatchEvent(new CustomEvent('open-project', { detail: { id: project.id } }));
  };

  return (
    <section className="relative w-full h-[85vh] bg-white pt-20 pb-0 overflow-hidden flex flex-col justify-center">
      <Swiper
        modules={[Autoplay, EffectCoverflow]}
        effect="coverflow"
        grabCursor={true}
        centeredSlides={true}
        slidesPerView="auto"
        loop={true}
        initialSlide={4}
        spaceBetween={0}
        speed={800}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
        }}
        coverflowEffect={{
          rotate: 0,
          stretch: 0,
          depth: 100,
          modifier: 2.5,
          slideShadows: false,
        }}
        slideToClickedSlide={true}
        className="w-full h-full"
      >
        {projects.map((project) => (
          <SwiperSlide 
            key={project.id} 
            className="w-[45vw] max-w-[600px] h-[75vh] relative transition-all duration-700 ease-out"
          >
            {({ isActive }) => (
              <div 
                className={`w-full h-full relative cursor-pointer transition-all duration-700 ease-out ${
                  isActive ? 'scale-100 opacity-100 z-10' : 'scale-[0.85] opacity-80 grayscale-[0.2] z-0'
                }`}
                onClick={() => isActive && handleProjectClick(project)}
              >
                <img 
                  src={project.image} 
                  alt={getLocalized(project, 'title', lang)}
                  className="w-full h-full object-cover object-center shadow-2xl"
                />
                
                {/* Text only on active slide */}
                <div 
                  className={`absolute bottom-0 left-0 w-full p-10 bg-gradient-to-t from-black/70 via-black/30 to-transparent text-white z-20 transition-all duration-700 ${
                    isActive ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                >
                  <h2 className="text-4xl md:text-5xl font-light mb-3 drop-shadow-lg tracking-tight">{getLocalized(project, 'title', lang)}</h2>
                  <p className="text-sm md:text-base uppercase tracking-widest opacity-90 drop-shadow-md font-medium">
                    {project.location} — {project.year}
                  </p>
                </div>
                
                {/* Overlay for inactive slides to darken them slightly */}
                {!isActive && (
                  <div className="absolute inset-0 bg-black/10 transition-colors duration-700" />
                )}
              </div>
            )}
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  );
};

export default Hero;
